import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Tag } from 'primereact/tag';
import { Dialog } from 'primereact/dialog';
import toast from 'react-hot-toast';
import PageHeader from '../../components/Shared/PageHeader';
import SharedButton from '../../components/Shared/SharedButton';
import { getERPClient } from '../../services/supplyChainApi';
import { openPrintWindow, buildSaleInvoicePrintHtml, erpBusinessToSellerSnapshot } from '../../helpers/invoicePrint';

const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const computeRefundAmount = (items, selected) => {
  const calcTotal = (item, refundQty) => {
    if (item.item_status === 'REFUNDED') return 0;
    const net = (Number(item.taxable_amount) || 0) + (Number(item.vat_amount) || 0);
    return net * ((Number(refundQty) || 0) / (Number(item.quantity) || 1));
  };
  const target = selected.length
    ? selected
        .map((ri) => ({ item: items.find((i) => i.item_id === ri.item_id), quantity: ri.quantity }))
        .filter((entry) => entry.item)
    : items;
  return target.reduce((s, entry) => {
    const item = entry.item || entry;
    return s + calcTotal(item, entry.quantity ?? item.quantity);
  }, 0);
};

export default function ERPSaleInvoiceDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const auth = useSelector((state) => state.auth);
  const business = auth?.user?.Business;

  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);

  const [cancelVisible, setCancelVisible] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  const [refundVisible, setRefundVisible] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [refundItems, setRefundItems] = useState([]);
  const [refunding, setRefunding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const erpClient = getERPClient();
      const res = await erpClient.getSaleInvoice(id);
      const data = res.data || res;
      setInv(data);
    } catch (err) {
      console.error('Failed to load sale invoice:', err);
      toast.error(t('Failed to load sale invoice'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const statusBody = () => {
    const status = inv?.status || 'DRAFT';
    const severity =
      status === 'CANCELLED' || status === 'REFUNDED'
        ? 'danger'
        : status === 'CONFIRMED'
          ? 'success'
          : 'info';
    return <Tag value={status} severity={severity} />;
  };

  const items = (inv && Array.isArray(inv.items) ? inv.items : []) || [];
  const grossTotal = items.reduce((s, i) => s + toNumber(i.quantity) * toNumber(i.unit_price), 0);
  const vatTotal = items.reduce((s, i) => s + toNumber(i.vat_amount || 0), 0);
  const discount = toNumber(inv?.discount || 0);
  const grand = toNumber(inv?.grand_total || inv?.total_amount || 0);

  const computedRefundAmount = computeRefundAmount(items, refundItems);

  const handleConfirmCancel = async () => {
    if (!cancelReason.trim()) return;
    setCancelling(true);
    try {
      const erpClient = getERPClient();
      await erpClient.cancelSaleInvoice(id, cancelReason);
      toast.success(t('Sale Invoice cancelled'));
      setCancelVisible(false);
      setCancelReason('');
      load();
    } catch (err) {
      const msg = err.response?.data?.message || t('Failed to cancel invoice');
      toast.error(msg);
    } finally {
      setCancelling(false);
    }
  };

  const toggleRefundItem = (itemId, quantity) => {
    setRefundItems((prev) => {
      const exists = prev.find((i) => i.item_id === itemId);
      if (exists) return prev.filter((i) => i.item_id !== itemId);
      return [...prev, { item_id: itemId, quantity }];
    });
  };

  const updateRefundQuantity = (itemId, quantity) =>
    setRefundItems((prev) =>
      prev.map((item) => (item.item_id === itemId ? { ...item, quantity } : item))
    );

  const handleConfirmRefund = async () => {
    if (!refundReason.trim() || computedRefundAmount <= 0) {
      toast.error(t('Please provide a refund reason and amount'));
      return;
    }
    setRefunding(true);
    try {
      const erpClient = getERPClient();
      const itemIds = refundItems.length
        ? refundItems
        : items.filter((it) => it.item_status !== 'REFUNDED').map((i) => ({ item_id: i.item_id, quantity: i.quantity }));
      await erpClient.refundSaleInvoice(id, {
        reason: refundReason,
        amount: Number(computedRefundAmount.toFixed(2)),
        items: itemIds.filter((it) => it.item_id),
      });
      toast.success(t('Sale Invoice refunded'));
      setRefundVisible(false);
      setRefundReason('');
      setRefundItems([]);
      load();
    } catch (err) {
      const msg = err.response?.data?.message || t('Failed to refund invoice');
      toast.error(msg);
    } finally {
      setRefunding(false);
    }
  };

  const handlePrint = async () => {
    if (!inv) return;
    const seller = erpBusinessToSellerSnapshot(business);
    const printInv = { ...inv, seller_snapshot: { ...(inv.seller_snapshot || {}), ...seller } };
    const html = await buildSaleInvoicePrintHtml(printInv, 'en');
    openPrintWindow(html, `Sale Invoice ${inv.invoice_number}`);
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-gray-500">
        <i className="pi pi-spin pi-spinner text-3xl mb-2" />
      </div>
    );
  }

  const isClosed = inv?.status === 'CANCELLED' || inv?.status === 'REFUNDED';
  const isConfirmed = inv?.status === 'CONFIRMED';

  return (
    <div className="p-4 space-y-4 max-w-[1600px] mx-auto">
      <PageHeader title={`${t('Sale Invoice')} #${inv?.invoice_number || ''}`} backPath="/manager/erp/sale-invoices">
        <div className="flex items-center gap-2">
          {inv?.status === 'REFUNDED' && <Tag value="REFUNDED" severity="danger" />}
          {isConfirmed && !isClosed && (
            <>
              <SharedButton
                type="button"
                label={t('Print')}
                icon="pi pi-print"
                buttonType="reset"
                onClick={handlePrint}
              />
              <SharedButton
                type="button"
                label={t('Refund')}
                icon="pi pi-undo"
                buttonType="primary"
                onClick={() => {
                  setRefundReason('');
                  setRefundItems([]);
                  setRefundVisible(true);
                }}
              />
              <SharedButton
                type="button"
                label={t('Cancel')}
                icon="pi pi-times"
                buttonType="danger"
                onClick={() => {
                  setCancelReason('');
                  setCancelVisible(true);
                }}
              />
            </>
          )}
        </div>
      </PageHeader>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-white border rounded-lg p-3">
          <span className="text-[10px] text-gray-500 uppercase tracking-wide block">{t('Invoice #')}</span>
          <p className="text-sm font-semibold mt-1">{inv?.invoice_number || '-'}</p>
        </div>
        <div className="bg-white border rounded-lg p-3">
          <span className="text-[10px] text-gray-500 uppercase tracking-wide block">{t('Date')}</span>
          <p className="text-sm font-semibold mt-1">{inv?.created_at ? String(inv.created_at).slice(0, 10) : '-'}</p>
        </div>
        <div className="bg-white border rounded-lg p-3">
          <span className="text-[10px] text-gray-500 uppercase tracking-wide block">{t('Status')}</span>
          <div className="mt-1">{statusBody()}</div>
        </div>
        <div className="bg-white border rounded-lg p-3">
          <span className="text-[10px] text-gray-500 uppercase tracking-wide block">{t('Payment Method')}</span>
          <p className="text-sm font-semibold mt-1">{inv?.payment_method || '-'}</p>
        </div>
        <div className="bg-white border rounded-lg p-3">
          <span className="text-[10px] text-gray-500 uppercase tracking-wide block">{t('Amount Paid')}</span>
          <p className="text-sm font-semibold mt-1">SAR {toNumber(inv?.amount_paid).toFixed(2)}</p>
        </div>
      </div>

      <div className="bg-white border rounded-lg p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div>
            <span className="text-xs text-gray-500 block">{t('Customer')}</span>
            <p className="font-semibold">{inv?.buyer_name || inv?.buyer_snapshot?.name || '-'}</p>
            <p className="text-xs text-gray-500">{inv?.buyer_phone || ''}</p>
          </div>
          <div>
            <span className="text-xs text-gray-500 block">{t('VAT Number')}</span>
            <p className="font-medium">{inv?.buyer_vat_number || inv?.buyer_snapshot?.vat_number || '-'}</p>
          </div>
          <div>
            <span className="text-xs text-gray-500 block">{t('CR Number')}</span>
            <p className="font-medium">{inv?.buyer_cr_number || inv?.buyer_snapshot?.cr_number || '-'}</p>
          </div>
          <div>
            <span className="text-xs text-gray-500 block">{t('Selling Location')}</span>
            <p className="font-medium">
              {inv?.selling_location_snapshot?.name_en ||
                inv?.selling_location_snapshot?.name_ar ||
                inv?.selling_location_snapshot?.code ||
                '-'}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white border rounded-lg">
        <h3 className="text-sm font-semibold p-4 border-b">{t('Items')}</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 text-gray-600">
                <th className="px-3 py-2 text-start">{t('#')}</th>
                <th className="px-3 py-2 text-start">{t('Item')}</th>
                <th className="px-3 py-2 text-start">{t('UOM')}</th>
                <th className="px-3 py-2 text-end">{t('Qty')}</th>
                <th className="px-3 py-2 text-end">{t('Sale Price')}</th>
                <th className="px-3 py-2 text-end">{t('Disc 1%')}</th>
                <th className="px-3 py-2 text-end">{t('Disc 2%')}</th>
                <th className="px-3 py-2 text-end">{t('VAT')}</th>
                <th className="px-3 py-2 text-end">{t('Total')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={idx} className="border-b">
                  <td className="px-3 py-2">{idx + 1}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium">{item.item_name || item.item_code || '-'}</div>
                    {item.item_status === 'REFUNDED' && (
                      <span className="text-[10px] text-red-500">({t('REFUNDED')})</span>
                    )}
                  </td>
                  <td className="px-3 py-2">{item.uom || item.uom_name || '-'}</td>
                  <td className="px-3 py-2 text-end">{toNumber(item.quantity)}</td>
                  <td className="px-3 py-2 text-end">{toNumber(item.unit_price).toFixed(2)}</td>
                  <td className="px-3 py-2 text-end">{toNumber(item.discount1_pct)}%</td>
                  <td className="px-3 py-2 text-end">{toNumber(item.discount2_pct)}%</td>
                  <td className="px-3 py-2 text-end">{toNumber(item.vat_amount || 0).toFixed(2)}</td>
                  <td className="px-3 py-2 text-end font-semibold">{toNumber(item.total_price || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex justify-end">
        <div className="w-full max-w-xs bg-white border rounded-lg p-4 text-sm">
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-500">{t('Gross')}</span>
              <span>{grossTotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">{t('Discount')}</span>
              <span className="text-red-500">-{discount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">{t('VAT')}</span>
              <span>{vatTotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-bold border-t pt-2">
              <span>{t('Total')}</span>
              <span className="text-primary">{grand.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>

      <Dialog
        header={t('Cancel Sale Invoice')}
        visible={cancelVisible}
        onHide={() => setCancelVisible(false)}
        pt={{
          root: { className: 'w-full max-w-md mx-4' },
          header: { className: '!p-2 !bg-darkBg !text-white' },
          content: { className: '!p-4' },
        }}
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-700">{t('Are you sure you want to cancel this sale invoice?')}</p>
          <textarea
            rows={3}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder={t('Cancellation reason')}
            className="w-full border border-gray-300 rounded p-2 text-sm focus:outline-none focus:border-red-500"
          />
          <div className="flex justify-end gap-2">
            <SharedButton type="button" label={t('Dismiss')} buttonType="reset" onClick={() => setCancelVisible(false)} />
            <SharedButton type="button" label={t('Confirm Cancellation')} buttonType="danger" loading={cancelling} onClick={handleConfirmCancel} />
          </div>
        </div>
      </Dialog>

      <Dialog
        header={t('Refund Sale Invoice')}
        visible={refundVisible}
        onHide={() => setRefundVisible(false)}
        pt={{
          root: { className: 'w-full lg:w-[560px] mx-4' },
          header: { className: '!p-2 !bg-darkBg !text-white' },
          content: { className: '!p-4' },
        }}
      >
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-gray-700 block mb-1">{t('Refund Amount')}</label>
            <div className="w-full border rounded-md px-3 py-2 text-sm bg-gray-50 font-semibold">
              {computedRefundAmount.toFixed(2)} SAR
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-700 block mb-1">{t('Reason')}</label>
            <textarea
              rows={2}
              value={refundReason}
              onChange={(e) => setRefundReason(e.target.value)}
              placeholder={t('Enter refund reason...')}
              className="w-full border border-gray-300 rounded p-2 text-xs focus:outline-none focus:border-purple-500"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-700 block mb-1">
              {t('Items to Return')} ({t('optional, select none to return all')})
            </label>
            <div className="border rounded-md max-h-[200px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="px-2 py-1 text-left w-8" />
                    <th className="px-2 py-1 text-left">{t('Item')}</th>
                    <th className="px-2 py-1 text-right">{t('Qty')}</th>
                    <th className="px-2 py-1 text-right">{t('Price')}</th>
                    <th className="px-2 py-1 text-right">{t('VAT')}</th>
                    <th className="px-2 py-1 text-right">{t('Total')}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const isRefunded = item.item_status === 'REFUNDED';
                    const net = (Number(item.taxable_amount) || 0) + (Number(item.vat_amount) || 0);
                    const checked = isRefunded || !!refundItems.find((i) => i.item_id === item.item_id);
                    const qty = refundItems.find((i) => i.item_id === item.item_id)?.quantity ?? item.unrefunded_quantity ?? item.quantity;
                    return (
                      <tr key={item.item_id || item.line_number} className={`border-b ${isRefunded ? 'opacity-50' : ''}`}>
                        <td className="px-2 py-1.5">
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={isRefunded}
                            onChange={() => toggleRefundItem(item.item_id, item.unrefunded_quantity ?? item.quantity)}
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          {item.item_name || item.item_code}
                          {isRefunded && <span className="text-red-500 text-[10px] ml-1">({t('REFUNDED')})</span>}
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <input
                            type="number"
                            min={0}
                            max={item.unrefunded_quantity ?? item.quantity}
                            disabled={isRefunded || !refundItems.find((i) => i.item_id === item.item_id)}
                            value={qty}
                            onChange={(e) =>
                              updateRefundQuantity(item.item_id, Math.min(Number(e.target.value) || 0, Number(item.unrefunded_quantity ?? item.quantity)))
                            }
                            className="h-6 w-14 text-right border border-gray-300 rounded p-1 text-xs"
                          />
                        </td>
                        <td className="px-2 py-1.5 text-right">{toNumber(item.unit_price).toFixed(2)}</td>
                        <td className="px-2 py-1.5 text-right">{toNumber(item.vat_amount || 0).toFixed(2)}</td>
                        <td className="px-2 py-1.5 text-right font-semibold">{net.toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <SharedButton
              type="button"
              label={t('Back')}
              buttonType="reset"
              onClick={() => {
                setRefundVisible(false);
                setRefundReason('');
                setRefundItems([]);
              }}
            />
            <SharedButton
              type="button"
              label={t('Process Refund')}
              buttonType="danger"
              loading={refunding}
              disabled={!refundReason.trim() || computedRefundAmount <= 0}
              onClick={handleConfirmRefund}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
}